import React, { useState, useMemo } from 'react';
import './TalentPlan.css';

/* ─── Static Configuration ──────────────────────────────────────────────────── */

const DEVELOPMENT_OPTIONS = [
  { id: 'short_course',       label: 'Short Course',          icon: 'bi-mortarboard',      description: 'Targeted skill-building workshops and courses' },
  { id: 'coaching_mentoring', label: 'Coaching and Mentoring',  icon: 'bi-people',           description: 'One-on-one guidance from experienced professionals' },
  { id: 'academic',           label: 'Academic Training',     icon: 'bi-building-check',   description: 'Formal academic programmes and qualifications' },
  { id: 'on_the_job',         label: 'On-the-Job Training',   icon: 'bi-briefcase',        description: 'Structured work-based learning experiences' },
  { id: 'e_learning',         label: 'e-Learning',            icon: 'bi-laptop',           description: 'Self-paced digital learning modules and platforms' },
  { id: 'secondment',         label: 'Secondment / Rotation', icon: 'bi-arrow-left-right', description: 'Temporary role placement for exposure and growth' },
];

const COMPETENCY_AREAS = [
  'Strategic Outlook',
  'Leadership and Management',
  'Interpersonal and Communication',
  'Digital and Innovation',
  'Operational Excellence',
  'Technical and Professional',
];

const STATUSES = ['All Statuses', 'Planned', 'In Progress', 'Completed', 'Cancelled'];

const STATUS_STYLES = {
  'Planned':     { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  'In Progress': { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  'Completed':   { bg: '#f0f7f4', color: '#006B54', border: '#6ee7b7' },
  'Cancelled':   { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
};

/* Mock "current user" profile */
const MY_PROFILE = {
  name:       'Amara Osei',
  role:       'Programs Officer',
  department: 'Programs',
  level:      'Entry Level',
  levelId:    1,
  initials:   'AO',
};

/* Mock individual plans (scoped to current user) */
const MY_PLANS = [
  {
    id: 1,
    competencyArea: 'Strategic Outlook',
    weakness: 'Vision and Conceptualisation of Strategy',
    currentScore: 52, targetScore: 75,
    developmentType: 'Coaching and Mentoring', developmentTypeId: 'coaching_mentoring',
    learningCenter: 'Internal Leadership Academy',
    courseName: 'Strategic Thinking Fundamentals',
    duration: '3 months', startDate: '2026-02-01', endDate: '2026-04-30',
    coach: 'Dr. Kofi Mensah', status: 'In Progress',
    notes: 'Bi-weekly sessions focused on environmental scanning and strategic formulation.',
  },
  {
    id: 2,
    competencyArea: 'Digital and Innovation',
    weakness: 'Data Analytics and Reporting',
    currentScore: 55, targetScore: 80,
    developmentType: 'e-Learning', developmentTypeId: 'e_learning',
    learningCenter: 'LinkedIn Learning',
    courseName: 'Excel and Power BI for Data Analysis',
    duration: '4 weeks', startDate: '2026-05-01', endDate: '2026-05-31',
    coach: '', status: 'Planned',
    notes: 'Self-paced; allocate 1 hour daily before team stand-up.',
  },
  {
    id: 3,
    competencyArea: 'Interpersonal and Communication',
    weakness: 'Report Writing and Documentation',
    currentScore: 60, targetScore: 82,
    developmentType: 'On-the-Job Training', developmentTypeId: 'on_the_job',
    learningCenter: 'Internal Mentorship Programme',
    courseName: 'Technical Writing and Reporting Skills',
    duration: '2 months', startDate: '2026-03-01', endDate: '2026-04-30',
    coach: 'James Kariuki', status: 'Completed',
    notes: 'Weekly writing assignments reviewed by supervisor. Certificate issued.',
  },
];

/* ─── Helpers ────────────────────────────────────────────────────────────────── */

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
  return (
    <span className="tdp-badge" style={{ background: s.bg, color: s.color, borderColor: s.border }}>
      {status}
    </span>
  );
}

function ScoreBar({ current, target }) {
  const color = current < 65 ? '#dc2626' : current < 75 ? '#f59e0b' : current < 90 ? '#3b82f6' : '#10b981';
  return (
    <div className="tdp-score-bar-wrap">
      <div className="tdp-score-values">
        <span style={{ color }}>Current: <strong>{current}%</strong></span>
        <span style={{ color: '#6b7280' }}>Target: <strong>{target}%</strong></span>
      </div>
      <div className="tdp-score-track">
        <div className="tdp-score-fill" style={{ width: `${Math.min(current, 100)}%`, background: color }} />
        <div className="tdp-score-target" style={{ left: `${Math.min(target, 100)}%` }} title={`Target: ${target}%`} />
      </div>
    </div>
  );
}

/* ─── Detail Drawer ──────────────────────────────────────────────────────────── */

function PlanDetail({ item, onClose }) {
  if (!item) return null;
  const devOpt = DEVELOPMENT_OPTIONS.find(d => d.id === item.developmentTypeId);
  return (
    <div className="tdp-overlay" onClick={onClose}>
      <div className="tdp-drawer" onClick={e => e.stopPropagation()}>
        <div className="tdp-drawer-header">
          <div>
            <h3 className="tdp-drawer-title">{item.competencyArea}</h3>
            <p className="tdp-drawer-subtitle">{item.weakness}</p>
          </div>
          <button className="tdp-close-btn" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="tdp-drawer-body">
          <div className="tdp-detail-grid">
            <div className="tdp-detail-block">
              <span className="tdp-detail-label">Plan Status</span>
              <StatusBadge status={item.status} />
            </div>
            <div className="tdp-detail-block">
              <span className="tdp-detail-label">Development Type</span>
              <span className="tdp-detail-value">{item.developmentType}</span>
            </div>
          </div>

          <div className="tdp-detail-section">
            <h4 className="tdp-section-title"><i className="bi bi-graph-up-arrow" /> Performance Gap</h4>
            <ScoreBar current={item.currentScore} target={item.targetScore} />
          </div>

          <div className="tdp-detail-section">
            <h4 className="tdp-section-title">
              <i className={`bi ${devOpt?.icon || 'bi-journal'}`} /> Learning Plan
            </h4>
            <div className="tdp-detail-grid">
              <div className="tdp-detail-block tdp-detail-full">
                <span className="tdp-detail-label">Learning Centre / Provider</span>
                <span className="tdp-detail-value">{item.learningCenter}</span>
              </div>
              <div className="tdp-detail-block tdp-detail-full">
                <span className="tdp-detail-label">Programme / Course Name</span>
                <span className="tdp-detail-value">{item.courseName}</span>
              </div>
              {item.coach && (
                <div className="tdp-detail-block tdp-detail-full">
                  <span className="tdp-detail-label">Assigned Coach / Mentor</span>
                  <span className="tdp-detail-value">{item.coach}</span>
                </div>
              )}
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">Duration</span>
                <span className="tdp-detail-value">{item.duration}</span>
              </div>
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">Start Date</span>
                <span className="tdp-detail-value">{item.startDate}</span>
              </div>
              <div className="tdp-detail-block">
                <span className="tdp-detail-label">End Date</span>
                <span className="tdp-detail-value">{item.endDate}</span>
              </div>
            </div>
          </div>

          {item.notes && (
            <div className="tdp-detail-section">
              <h4 className="tdp-section-title"><i className="bi bi-chat-text" /> Notes</h4>
              <p className="tdp-notes-text">{item.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Add Goal Form ──────────────────────────────────────────────────────────── */

const EMPTY_FORM = {
  competencyArea: '', weakness: '', currentScore: '', targetScore: '',
  developmentTypeId: '', learningCenter: '', courseName: '',
  duration: '', startDate: '', endDate: '', coach: '',
  status: 'Planned', notes: '',
};

const FORM_STEPS = ['Growth Area', 'Intervention', 'Review and Confirm'];

function AddGoalForm({ onClose, onSave }) {
  const [step, setStep]         = useState(1);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [feedback, setFeedback] = useState({ msg: '', type: '' });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    const rules = [
      [1, (!form.competencyArea || !form.weakness || !form.currentScore || !form.targetScore), 'Please complete all growth area fields.'],
      [2, (!form.developmentTypeId || !form.learningCenter || !form.courseName), 'Please select a development type and provide the learning centre and course name.'],
    ];
    for (const [s, invalid, msg] of rules) {
      if (step === s && invalid) { setFeedback({ msg, type: 'error' }); return false; }
    }
    setFeedback({ msg: '', type: '' });
    return true;
  };

  const next   = () => { if (validate()) setStep(s => s + 1); };
  const back   = () => { setStep(s => s - 1); setFeedback({ msg: '', type: '' }); };

  const submit = () => {
    if (!validate()) return;
    const devObj = DEVELOPMENT_OPTIONS.find(d => d.id === form.developmentTypeId);
    onSave({
      ...form,
      id: Date.now(),
      developmentType: devObj?.label || '',
      currentScore: parseInt(form.currentScore),
      targetScore:  parseInt(form.targetScore),
    });
    setFeedback({ msg: 'Development goal added to your plan.', type: 'success' });
    setTimeout(onClose, 1100);
  };

  const reviewField = (label, val) => val ? (
    <div className="tdp-review-row">
      <span className="tdp-review-label">{label}</span>
      <span className="tdp-review-val">{val}</span>
    </div>
  ) : null;

  return (
    <div className="tdp-overlay" onClick={onClose}>
      <div className="tdp-modal" onClick={e => e.stopPropagation()}>
        <div className="tdp-drawer-header">
          <div>
            <h3 className="tdp-drawer-title">Add Development Goal</h3>
            <p className="tdp-drawer-subtitle">Set a new growth target for your personal development plan</p>
          </div>
          <button className="tdp-close-btn" onClick={onClose} aria-label="Close"><i className="bi bi-x-lg" /></button>
        </div>

        <div className="tdp-stepbar">
          {FORM_STEPS.map((s, i) => (
            <div key={s} className={`tdp-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
              <div className="tdp-step-circle">
                {step > i + 1 ? <i className="bi bi-check" /> : i + 1}
              </div>
              <span className="tdp-step-lbl">{s}</span>
              {i < FORM_STEPS.length - 1 && <div className="tdp-step-line" />}
            </div>
          ))}
        </div>

        <div className="tdp-modal-body">
          {feedback.msg && (
            <div className={`tdp-feedback tdp-fb-${feedback.type}`}>{feedback.msg}</div>
          )}

          {/* Step 1 — Growth Area */}
          {step === 1 && (
            <div className="tdp-form-grid">
              <div className="tdp-fg">
                <label>Competency Area *</label>
                <select value={form.competencyArea} onChange={e => set('competencyArea', e.target.value)}>
                  <option value="">Select area...</option>
                  {COMPETENCY_AREAS.map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div className="tdp-fg">
                <label>Specific Area of Weakness *</label>
                <input value={form.weakness} onChange={e => set('weakness', e.target.value)} placeholder="e.g. Report Writing and Documentation" />
              </div>
              <div className="tdp-fg">
                <label>Current Score (%) *</label>
                <input type="number" min={0} max={100} value={form.currentScore} onChange={e => set('currentScore', e.target.value)} placeholder="e.g. 55" />
              </div>
              <div className="tdp-fg">
                <label>Target Score (%) *</label>
                <input type="number" min={0} max={100} value={form.targetScore} onChange={e => set('targetScore', e.target.value)} placeholder="e.g. 80" />
              </div>
              {form.currentScore && form.targetScore && (
                <div className="tdp-fg tdp-fg-full">
                  <label>Gap Preview</label>
                  <ScoreBar current={parseInt(form.currentScore) || 0} target={parseInt(form.targetScore) || 0} />
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Intervention */}
          {step === 2 && (
            <div className="tdp-form-grid">
              <div className="tdp-fg tdp-fg-full">
                <label>Development Type *</label>
                <div className="tdp-devtype-picker">
                  {DEVELOPMENT_OPTIONS.map(d => (
                    <label key={d.id} className={`tdp-dt-card ${form.developmentTypeId === d.id ? 'selected' : ''}`}>
                      <input type="radio" name="devt" value={d.id} checked={form.developmentTypeId === d.id} onChange={e => set('developmentTypeId', e.target.value)} />
                      <i className={`bi ${d.icon} tdp-dt-icon`} />
                      <div className="tdp-dt-name">{d.label}</div>
                      <div className="tdp-dt-desc">{d.description}</div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="tdp-fg">
                <label>Learning Centre / Provider *</label>
                <input value={form.learningCenter} onChange={e => set('learningCenter', e.target.value)} placeholder="e.g. LinkedIn Learning" />
              </div>
              <div className="tdp-fg">
                <label>Programme / Course Name *</label>
                <input value={form.courseName} onChange={e => set('courseName', e.target.value)} placeholder="e.g. Technical Writing Skills" />
              </div>
              {form.developmentTypeId === 'coaching_mentoring' && (
                <div className="tdp-fg">
                  <label>Assigned Coach / Mentor</label>
                  <input value={form.coach} onChange={e => set('coach', e.target.value)} placeholder="e.g. James Kariuki" />
                </div>
              )}
              <div className="tdp-fg">
                <label>Duration</label>
                <input value={form.duration} onChange={e => set('duration', e.target.value)} placeholder="e.g. 4 weeks" />
              </div>
              <div className="tdp-fg">
                <label>Start Date</label>
                <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
              </div>
              <div className="tdp-fg">
                <label>End Date</label>
                <input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
              </div>
              <div className="tdp-fg tdp-fg-full">
                <label>Personal Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Any personal reminders or context for this goal..." />
              </div>
            </div>
          )}

          {/* Step 3 — Review */}
          {step === 3 && (
            <div className="tdp-review">
              <div className="tdp-review-section">
                <h4><i className="bi bi-bullseye" /> Growth Area</h4>
                {reviewField('Competency Area', form.competencyArea)}
                {reviewField('Area of Weakness', form.weakness)}
                {reviewField('Current Score', `${form.currentScore}%`)}
                {reviewField('Target Score', `${form.targetScore}%`)}
              </div>
              <div className="tdp-review-section">
                <h4><i className="bi bi-journal-check" /> Learning Plan</h4>
                {reviewField('Development Type', DEVELOPMENT_OPTIONS.find(d => d.id === form.developmentTypeId)?.label)}
                {reviewField('Learning Centre', form.learningCenter)}
                {reviewField('Course Name', form.courseName)}
                {reviewField('Coach / Mentor', form.coach)}
                {reviewField('Duration', form.duration)}
                {reviewField('Start Date', form.startDate)}
                {reviewField('End Date', form.endDate)}
              </div>
              {form.notes && (
                <div className="tdp-review-section">
                  <h4><i className="bi bi-chat-text" /> Notes</h4>
                  <p className="tdp-notes-text">{form.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="tdp-modal-footer">
          {step > 1 && <button className="tdp-btn-outline" onClick={back}><i className="bi bi-arrow-left" /> Back</button>}
          <span style={{ flex: 1 }} />
          {step < 3 && (
            <button className="tdp-btn-primary" onClick={next}>
              Next <i className="bi bi-arrow-right" />
            </button>
          )}
          {step === 3 && (
            <button className="tdp-btn-primary" onClick={submit}>
              <i className="bi bi-check-lg" /> Add Goal
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────────── */

export default function IndividualTalentPlan() {
  const [plans,        setPlans]        = useState(MY_PLANS);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [filterStatus, setFilterStatus] = useState('All Statuses');
  const [search,       setSearch]       = useState('');

  const filtered = useMemo(() => plans.filter(i => {
    if (filterStatus !== 'All Statuses' && i.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!i.competencyArea.toLowerCase().includes(q) &&
          !i.weakness.toLowerCase().includes(q) &&
          !i.courseName.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [plans, filterStatus, search]);

  const stats = useMemo(() => ({
    total:      plans.length,
    planned:    plans.filter(i => i.status === 'Planned').length,
    inProgress: plans.filter(i => i.status === 'In Progress').length,
    completed:  plans.filter(i => i.status === 'Completed').length,
  }), [plans]);

  const overallProgress = useMemo(() => {
    if (!plans.length) return 0;
    const avg = plans.reduce((sum, p) => sum + (p.currentScore / p.targetScore), 0) / plans.length;
    return Math.round(Math.min(avg * 100, 100));
  }, [plans]);

  return (
    <div className="tdp-root">

      {/* ── Header ── */}
      <div className="tdp-header">
        <div className="tdp-header-left">
          <div className="tdp-breadcrumb">
            <i className="bi bi-person-lines-fill" /> Talent Development
            <i className="bi bi-chevron-right" /> Individual
          </div>
          <h1 className="tdp-title">My Development Plan</h1>
          <p className="tdp-subtitle">
            Your personal talent development journey — track growth goals, learning interventions, and competency progress
          </p>
        </div>
        <button className="tdp-btn-primary" onClick={() => setShowCreate(true)}>
          <i className="bi bi-plus-lg" /> Add Goal
        </button>
      </div>

      {/* ── My Profile Card ── */}
      <div className="tdp-my-profile">
        <div className="tdp-my-avatar">{MY_PROFILE.initials}</div>
        <div>
          <p className="tdp-my-name">{MY_PROFILE.name}</p>
          <p className="tdp-my-role">{MY_PROFILE.role}</p>
          <span className="tdp-my-dept">{MY_PROFILE.department} · {MY_PROFILE.level}</span>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#006B54' }}>{overallProgress}%</div>
          <div style={{ fontSize: 12, color: '#6b8f84' }}>Overall Progress</div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="tdp-stats-row">
        {[
          { label: 'Total Goals',  value: stats.total,      icon: 'bi-journal-richtext', color: '#006B54' },
          { label: 'Planned',      value: stats.planned,    icon: 'bi-clock',            color: '#1d4ed8' },
          { label: 'In Progress',  value: stats.inProgress, icon: 'bi-arrow-repeat',     color: '#15803d' },
          { label: 'Completed',    value: stats.completed,  icon: 'bi-check-circle',     color: '#059669' },
        ].map(s => (
          <div key={s.label} className="tdp-stat-card">
            <i className={`bi ${s.icon} tdp-stat-icon`} style={{ color: s.color }} />
            <div className="tdp-stat-val" style={{ color: s.color }}>{s.value}</div>
            <div className="tdp-stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Progress Snapshot ── */}
      <div className="tdp-progress-section">
        <div className="tdp-progress-title">
          <i className="bi bi-bar-chart-steps" style={{ marginRight: 7 }} />
          Competency Progress Snapshot
        </div>
        {plans.map(p => {
          const devOpt = DEVELOPMENT_OPTIONS.find(d => d.id === p.developmentTypeId);
          const pct    = Math.round((p.currentScore / p.targetScore) * 100);
          const statusStyle = STATUS_STYLES[p.status] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
          return (
            <div key={p.id} className="tdp-progress-item">
              <div className="tdp-pi-icon" style={{ background: '#e8f5f0', color: '#006B54' }}>
                <i className={`bi ${devOpt?.icon || 'bi-journal'}`} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="tdp-pi-label">{p.competencyArea}</div>
                <div className="tdp-pi-sub">{p.weakness}</div>
                <div style={{ marginTop: 6 }}>
                  <ScoreBar current={p.currentScore} target={p.targetScore} />
                </div>
              </div>
              <div className="tdp-pi-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <span className="tdp-badge" style={{ background: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border }}>
                  {p.status}
                </span>
                <span style={{ fontSize: 11, color: '#6b8f84' }}>{pct}% to target</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Filters ── */}
      <div className="tdp-filters">
        <div className="tdp-search-wrap">
          <i className="bi bi-search tdp-search-icon" />
          <input
            className="tdp-search"
            placeholder="Search goals, courses, competency areas..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="tdp-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* ── Table ── */}
      <div className="tdp-table-wrap">
        <table className="tdp-table">
          <thead>
            <tr>
              <th>Competency Area</th>
              <th>Growth Gap</th>
              <th>Development Type</th>
              <th>Course / Programme</th>
              <th>Duration</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="tdp-empty">
                  <i className="bi bi-inbox tdp-empty-icon" />
                  <div>No development goals found.</div>
                </td>
              </tr>
            ) : filtered.map(item => {
              const devOpt = DEVELOPMENT_OPTIONS.find(d => d.id === item.developmentTypeId);
              return (
                <tr key={item.id}>
                  <td>
                    <div className="tdp-emp-name">{item.competencyArea}</div>
                  </td>
                  <td>
                    <div className="tdp-gap-weak">{item.weakness}</div>
                    <div style={{ marginTop: 4, width: 120 }}>
                      <ScoreBar current={item.currentScore} target={item.targetScore} />
                    </div>
                  </td>
                  <td>
                    <div className="tdp-dt-cell">
                      <i className={`bi ${devOpt?.icon || 'bi-journal'}`} />
                      <span>{item.developmentType}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{item.courseName}</div>
                    <div className="tdp-muted">{item.learningCenter}</div>
                  </td>
                  <td className="tdp-muted">{item.duration}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>
                    <button className="tdp-view-btn" onClick={() => setSelectedItem(item)}>
                      <i className="bi bi-eye" /> View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedItem && (
        <PlanDetail item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
      {showCreate && (
        <AddGoalForm
          onClose={() => setShowCreate(false)}
          onSave={item => setPlans(prev => [item, ...prev])}
        />
      )}
    </div>
  );
}
